/**
 * Authentication Context
 *
 * Provides global authentication state and methods
 * to all components in the app
 * SECURITY: Initializes CSRF token on mount for protection against CSRF attacks
 */

import { createContext, ComponentChildren } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { authService, type User } from '../services/authService';
import { csrfService } from '../services/csrfService';

export interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ComponentChildren;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  /**
   * Initialize authentication state on mount
   * SECURITY: Also initializes CSRF token
   */
  useEffect(() => {
    const initAuth = async () => {
      // SECURITY: Initialize CSRF token early
      // This ensures token is available for any subsequent requests
      try {
        await csrfService.fetchToken();
        console.log('[AuthContext] CSRF token initialized');
      } catch (error) {
        console.error('[AuthContext] Failed to initialize CSRF token:', error);
        // Continue anyway - CSRF might be disabled in development
      }

      const storedToken = authService.getToken();

      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        // Verify token and get user data
        const result = await authService.verifyToken();
        setUser(result.user);
        setToken(storedToken);
      } catch (error) {
        console.error('Token verification failed:', error);
        // Clear invalid token
        authService.clearAuth();
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for unauthorized events (from apiClient)
    const handleUnauthorized = () => {
      setUser(null);
      setToken(null);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  /**
   * Login user
   */
  const login = async (email: string, password: string): Promise<void> => {
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      setToken(response.token);
    } catch (error) {
      // Re-throw error to be handled by component
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (email: string, password: string): Promise<void> => {
    setLoading(true);

    try {
      const response = await authService.register(email, password);
      // Note: Registration doesn't automatically log in
      // User will need to login after registering
      console.log('User registered:', response.user);
    } catch (error) {
      // Re-throw error to be handled by component
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async (): Promise<void> => {
    setLoading(true);

    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setLoading(false);
    }
  };

  /**
   * Refresh user data
   */
  const refreshUser = async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const userData = await authService.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // If refresh fails, logout user
      await logout();
    }
  };

  const value: AuthContextValue = {
    user,
    token,
    loading,
    isAuthenticated: user !== null && token !== null,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
